import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'app/components/Button/ButtonVariant';
import { ProductTypeLabel } from 'app/components/ProductTypeLabel';
import { generateCurrencySymbol } from 'app/routes/BuildProgram/Pricing/utils';
import { scheduledFirstChargeNote } from 'app/routes/Billing/NewOffering/utils';
import { canPurchaseOnline, packageCtaKey } from '../../../services/purchasability';
import { Package } from '../../../services/api';
import sessionIcon from '../../../assets/session.svg';
import styles from './styles.module.scss';

// Description with a "Read more..." link that appears only when the text
// overflows the clamped height. The link opens the package details modal.
const OverviewDescription: React.FC<{ text: string; onReadMore: () => void }> = ({
  text,
  onReadMore,
}) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (el) {
        setIsTruncated(el.scrollHeight > el.clientHeight + 1);
      }
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [text]);

  return (
    <div className={styles.descriptionWrap}>
      <p ref={ref} className={styles.description}>{text}</p>
      {isTruncated && (
        <button type="button" className={styles.readMore} onClick={onReadMore}>
          {t('Read more...')}
        </button>
      )}
    </div>
  );
};

/**
 * What the package buys, across from its badge (43552:83844).
 *
 * It used to be a grey bar under the title carrying the rate's own name —
 * "45 mins - 45 Min. - Adult" — with an ellipsis, a tooltip for the part that
 * did not fit, and a "Details" link when the package covered several rates.
 * The frame keeps the count and drops the rest: the name of the option is on
 * the card the athlete books it from, and everything the bar linked to is still
 * a click away through the description's Read more or the button below it.
 */
const SessionCount: React.FC<{ pkg: Package }> = ({ pkg }) => {
  const { t } = useTranslation();
  const rateLinks = pkg.rateLinks ?? [];

  if (rateLinks.length === 0) {
    return null;
  }

  // One rate with no quantity, or several adding up to none, is an unlimited
  // package — a subscription, which the frame writes as "∞ Unlimited".
  const total = rateLinks.reduce((sum, link) => sum + (link.quantity ?? 0), 0);
  const unlimited =
    rateLinks.length === 1 ? rateLinks[0].quantity == null : total === 0;
  const count = rateLinks.length === 1 ? rateLinks[0].quantity : total;

  return (
    <div className={styles.sessions}>
      {unlimited ? (
        <span className={styles.infinity}>&infin;</span>
      ) : (
        <img src={sessionIcon} alt="" className={styles.sessionsIcon} />
      )}
      <span className={styles.sessionsCount}>
        {unlimited ? t('Unlimited') : `${count} ${t('Sessions')}`}
      </span>
    </div>
  );
};

interface OverviewPackageCardProps {
  pkg: Package;
  // Opens the package details modal for this package (parent owns modal state).
  onOpenDetails: () => void;
}

const OverviewPackageCard: React.FC<OverviewPackageCardProps> = ({ pkg, onOpenDetails }) => {
  const { t } = useTranslation();
  const purchasable = canPurchaseOnline(pkg);
  // CN-2832: a custom start date bills nothing at checkout.
  const firstChargeNote = scheduledFirstChargeNote(
    pkg,
    `${generateCurrencySymbol(pkg.currency)}${pkg.price}`,
    t,
  );

  return (
    <div className={styles.card}>
      {/* The badge and what the package buys, on one line, with the title under
          them (43552:83841). */}
      <div className={styles.top}>
        <div className={styles.label}>
          <ProductTypeLabel product={pkg} enabledIntegration={true} />
        </div>
        <SessionCount pkg={pkg} />
      </div>
      <h3 className={styles.title}>{pkg.title}</h3>
      <OverviewDescription text={pkg.description} onReadMore={onOpenDetails} />
      <hr className={styles.divider} />
      {/*
        What it costs and the way to buy it, on one line (43552:83850) — the
        price on the left and the button on the right, which is how every other
        card on this page ends. They used to stack, with the button across the
        full width under the price.
      */}
      <div className={styles.bottom}>
        <div className={styles.priceInfo}>
          <span className={styles.price}>
            {generateCurrencySymbol(pkg.currency)}{pkg.price}
          </span>
          <span className={styles.paymentInfo}>
            {pkg.productType === 'product'
              ? t('One-time payment')
              : pkg.recurring === '1'
                ? t('Billed monthly')
                : pkg.recurring === '12'
                  ? t('Billed annually')
                  : t('Billed every {{count}} months', { count: Number(pkg.recurring) })}
          </span>
          {firstChargeNote && <span className={styles.paymentInfo}>{firstChargeNote}</span>}
        </div>
        <div className={styles.cta}>
          <Button
            buttonType="secondary-filled"
            buttonStyles={purchasable ? undefined : styles.offline}
            title={t(packageCtaKey(pkg))}
            onClickFunc={onOpenDetails}
          />
        </div>
      </div>
    </div>
  );
};

export default OverviewPackageCard;
